import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getMyOrders,
  getAllOrders,
  createOrder as createOrderService,
  updateOrderStatus as updateOrderStatusService,
  updateOrderNotes as updateOrderNotesService,
} from '../services/orders';
import { logError } from '../utils/errorHandler';

/**
 * Custom hook for fetching and managing orders
 * Automatically uses the correct endpoint based on user role (customer vs employee)
 *
 * @param {Object} options - Hook options
 * @param {boolean} options.autoFetch - Whether to automatically fetch on mount (default: true)
 * @param {Object} options.filters - Optional filters to apply
 * @returns {Object} - Orders state and functions
 */
const useOrders = ({ autoFetch = true, filters = {} } = {}) => {
  const { isEmployee, isAuthenticated } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(autoFetch && isAuthenticated);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Fetch orders from the API (role-based)
   */
  const fetchOrders = useCallback(async (customFilters = {}) => {
    if (!isAuthenticated) {
      setOrders([]);
      setLoading(false);
      return;
    }

    try {
      setError(null);

      // If this is a refresh, set isRefreshing instead of loading
      if (orders.length > 0) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      const mergedFilters = { ...filters, ...customFilters };

      // Use appropriate endpoint based on role
      const data = isEmployee
        ? await getAllOrders(mergedFilters)
        : await getMyOrders(mergedFilters);

      setOrders(Array.isArray(data) ? data : (data.results || []));
    } catch (err) {
      logError('Fetch orders', err);
      setError(err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [isAuthenticated, isEmployee, filters, orders.length]);

  /**
   * Create a new order
   * @param {Object} orderData - Order data to create
   * @returns {Promise<Object>} - Created order
   */
  const createOrder = useCallback(async (orderData) => {
    try {
      setError(null);
      setIsCreating(true);

      const newOrder = await createOrderService(orderData);

      // Add new order to the beginning of the list
      setOrders(prevOrders => [newOrder, ...prevOrders]);

      return newOrder;
    } catch (err) {
      logError('Create order', err);
      setError(err);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, []);

  /**
   * Update order status (employee only)
   * @param {string} orderId - Order ID
   * @param {string} status - New status
   * @returns {Promise<Object>} - Updated order
   */
  const updateStatus = useCallback(async (orderId, status) => {
    if (!isEmployee) {
      throw new Error('Only employees can update order status');
    }

    try {
      setError(null);
      setIsUpdating(true);

      const updatedOrder = await updateOrderStatusService(orderId, status);

      // Update order in the list
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? { ...order, status: updatedOrder.status } : order
        )
      );

      return updatedOrder;
    } catch (err) {
      logError('Update order status', err);
      setError(err);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [isEmployee]);

  /**
   * Update order internal notes (employee only)
   * @param {string} orderId - Order ID
   * @param {string} notes - New notes
   * @returns {Promise<Object>} - Updated order
   */
  const updateNotes = useCallback(async (orderId, notes) => {
    if (!isEmployee) {
      throw new Error('Only employees can update order notes');
    }

    try {
      setError(null);
      setIsUpdating(true);

      const updatedOrder = await updateOrderNotesService(orderId, notes);

      // Update order in the list
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId
            ? { ...order, internal_notes: updatedOrder.internal_notes }
            : order
        )
      );

      return updatedOrder;
    } catch (err) {
      logError('Update order notes', err);
      setError(err);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [isEmployee]);

  /**
   * Refresh orders (re-fetch from API)
   */
  const refresh = useCallback(() => {
    return fetchOrders();
  }, [fetchOrders]);

  /**
   * Get a single order by ID from the cached orders
   */
  const getOrderById = useCallback((orderId) => {
    return orders.find(o => o.id === orderId);
  }, [orders]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-fetch orders on mount if enabled and user is authenticated
  useEffect(() => {
    if (autoFetch && isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]); // Re-fetch if auth state changes

  return {
    orders,
    loading,
    error,
    isRefreshing,
    isCreating,
    isUpdating,
    fetchOrders,
    createOrder,
    updateStatus,
    updateNotes,
    refresh,
    getOrderById,
    clearError,
  };
};

export default useOrders;
